import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { DeliveryOptions } from './DeliveryOptions';
import { FreeDeliveryToggle } from './FreeDeliveryToggle';
import { useDeliveryOptionsContext } from '../../context/DeliveryOptionsContext';
import {
  getDeliveryDimension, getSingleDeliveryRefinement, toTitleCase
} from '../../utils/deliveryUtils';
import {
  getAppliedDeliveryDimension
} from '../../utils/fulfillment-helpers';
import { DELIVERY_REFINEMENTS, FULFILLMENT_DIMENSIONS_LABELS, FULFILLMENT_LABELS } from '../../utils/constants';

const {
  bool, func, string, obj
} = PropTypes;

const DeliveryRefinements = ({
  className,
  customStyles,
  onDimensionChange,
  onRefinementChange,
  skipDeliveryAutoSelect,
  addMarkupForSeo,
  deselect
}) => {
  const {
    selectedDeliveryDimension,
    setSelectedDeliveryDimension,
    isFreeDeliveryChecked,
    setIsFreeDeliveryChecked,
    deliveryOptions,
    hideDeliveryOptions,
    freeDeliveryUnavailable,
    appliedDimensions,
    triggerDeliverySpeedChange,
    setTriggerDeliverySpeedChange,
    dimensions,
    forceInitialSync,
    setForceInitialSync
  } = useDeliveryOptionsContext();
  const freeDeliveryRefinement = getSingleDeliveryRefinement(
    dimensions, FULFILLMENT_DIMENSIONS_LABELS.freeDelivery,
    DELIVERY_REFINEMENTS.ANYTIME
  );
  const [localIsFreeDeliveryChecked, setLocalIsFreeDeliveryChecked] = useState(isFreeDeliveryChecked);

  const syncUi = useCallback((updatedDimensions) => {
    const appliedDeliveryDimension = getAppliedDeliveryDimension(updatedDimensions);

    setLocalIsFreeDeliveryChecked(appliedDeliveryDimension?.label === FULFILLMENT_LABELS.FREE_DELIVERY);
    setTriggerDeliverySpeedChange(appliedDeliveryDimension?.refinements[0]?.label || null);
  }, [setLocalIsFreeDeliveryChecked, setTriggerDeliverySpeedChange]);

  const handleFreeDeliveryToggle = useCallback((isTrue) => {
    setLocalIsFreeDeliveryChecked(isTrue);
    const newDeliveryDimensionLabel = isTrue ? 'freeDelivery' : 'delivery';
    setSelectedDeliveryDimension(newDeliveryDimensionLabel);

    const appliedDeliveryDimension = getAppliedDeliveryDimension(appliedDimensions);
    const dimension = getDeliveryDimension(
      dimensions,
      FULFILLMENT_DIMENSIONS_LABELS[newDeliveryDimensionLabel]
    );

    let refinement = getSingleDeliveryRefinement(
      dimensions,
      FULFILLMENT_DIMENSIONS_LABELS[newDeliveryDimensionLabel],
      appliedDeliveryDimension?.refinements[0]?.label || DELIVERY_REFINEMENTS.ANYTIME
    );
    refinement = { ...refinement, isToggle: true };
    onDimensionChange({ refinement, dimension });
  }, [
    onDimensionChange,
    appliedDimensions,
    dimensions,
    setSelectedDeliveryDimension
  ]);

  const handleRefinementOptionChange = useCallback((label) => {
    if (onRefinementChange) {
      const dimension = getDeliveryDimension(
        dimensions,
        FULFILLMENT_DIMENSIONS_LABELS[selectedDeliveryDimension]
      );
      const selectedRefinement = getSingleDeliveryRefinement(
        dimensions,
        FULFILLMENT_DIMENSIONS_LABELS[selectedDeliveryDimension],
        label
      );

      if (!selectedRefinement) {
        return;
      }

      onRefinementChange({
        refinement: {
          label,
          url: selectedRefinement?.url,
          refinementKey: selectedRefinement?.refinementKey
        },
        dimension: {
          ...dimension,
          label: toTitleCase(FULFILLMENT_DIMENSIONS_LABELS[selectedDeliveryDimension])
        }
        // Pending: Confirm if onDisplay property is needed
        // onDisplay
      });
    }
  }, [onRefinementChange, dimensions, selectedDeliveryDimension]);

  useEffect(() => {
    if (deselect) {
      setLocalIsFreeDeliveryChecked(false);
      setIsFreeDeliveryChecked(false);
      setSelectedDeliveryDimension('delivery');
    }
  }, [deselect, setIsFreeDeliveryChecked, setSelectedDeliveryDimension]);

  // Force sync local state with context state when appliedDimensions changes
  useEffect(() => {
    syncUi(appliedDimensions);
  }, [appliedDimensions, syncUi]);

  // Force sync local UI state with context state when forceInitialSync is true.
  // Currently used when opening the Secondary Filters Drawer.
  useEffect(() => {
    if (forceInitialSync && !deselect) {
      syncUi(appliedDimensions);
      setForceInitialSync(false);
    }
  }, [
    appliedDimensions,
    deselect,
    forceInitialSync,
    setForceInitialSync,
    syncUi
  ]);

  return (
    <div
      className={classNames('sui-flex sui-flex-col sui-gap-x-4 sui-gap-y-3 sui-w-full [&>label]:sui-mx-0', className)}
      style={customStyles}
    >
      {!freeDeliveryUnavailable && (
        <>
          {/* SEO-friendly hidden link - Required for SEO significance. Reach out to SEO team if wanting to change */}
          {addMarkupForSeo && (
            <div data-testid="emt-link-free-delivery">
              <a
                href={freeDeliveryRefinement?.url}
                className="sui-hidden"
                aria-hidden="true"
                tabIndex="-1"
              >
                {/* H3 element required for SEO significance. Reach out to SEO team if wanting to change */}
                <h3>{FULFILLMENT_LABELS.FREE_DELIVERY}</h3>
              </a>
            </div>
          )}
          <FreeDeliveryToggle isChecked={localIsFreeDeliveryChecked} onToggle={handleFreeDeliveryToggle} />
        </>
      )}
      {!hideDeliveryOptions && deliveryOptions?.length > 0 && (
        <DeliveryOptions
          onChange={handleRefinementOptionChange}
          options={deliveryOptions}
          skipAutoSelect={skipDeliveryAutoSelect && deselect}
          triggerDeliverySpeedChange={triggerDeliverySpeedChange}
          setTriggerDeliverySpeedChange={setTriggerDeliverySpeedChange}
          deselect={deselect}
        />
      )}
    </div>
  );
};

DeliveryRefinements.propTypes = {
  className: string,
  customStyles: obj,
  onDimensionChange: func.isRequired,
  onRefinementChange: func,
  skipDeliveryAutoSelect: bool,
  addMarkupForSeo: bool,
  deselect: bool
};

DeliveryRefinements.defaultProps = {
  className: '',
  customStyles: {},
  onRefinementChange: null,
  skipDeliveryAutoSelect: false,
  addMarkupForSeo: false,
  deselect: false
};

DeliveryRefinements.displayName = 'DeliveryRefinements';

export { DeliveryRefinements };